// 헬 보스 「지옥불 각인」 연출(C10, game/hellBrand.ts). 규칙은 Ev.HellBrand와 view.hellBrand만 읽는다.
//  - 지옥불 장판: InstancedMesh 1개(최대 HELL_BRAND.max = 4칸). 검은 껍질 + 진홍 균열 + 주황 불꽃 테. 남은 0.5초 동안 식으며 옅어진다.
//  - 새김(경고) 룬 원: 기존 텔레그래프 데칼 위에 덧그리는 가산 룬 + 경고 길이 동안 안쪽으로 조여드는 고리(Mesh 1개, 경고는 동시에 하나뿐).
//  - 폭발: 충격파 링(Mesh 1개) + 불기둥·불씨·불똥 줄·불꽃 덩이(raidVfx의 RaidParticles 가산 풀 재사용) + 아주 작은 카메라 펀치.
// 입자 예산(품질 배율 s 적용): 폭발 1회 = 불씨 16 + 불똥 줄 10 + 기둥 2 + 광휘 1 + 불꽃 덩이 4 = 33개, 장판 4개의 불씨/불꽃 상시 방출은 풀 상한 안에서만(풀이 차면 버림).
// 가산 풀 상한 = PARTICLE_CAP.add × clamp(s,.25,.3) ≥ 180. 프레임 루프 안에서 새 객체를 만들지 않는다. 섬광은 폭발 순간 광휘 1회뿐(7초 간격)이라 1초 3회 이하.
import * as THREE from 'three/webgpu';
import { atan, attribute, float, fract, mix, sin, smoothstep, uniform, uv, vec2, vec3 } from 'three/tsl';
import { Ev, type GameEvent } from '../shared/events';
import type { GameView } from '../shared/types';
import { HELL_BRAND, HellBrandPhase } from '../game/hellBrand';
import { AddKind, Orient, RaidParticles } from './raidVfxParticles';

type N = any; // eslint-disable-line @typescript-eslint/no-explicit-any

/** 장판 판 크기 배율(바깥 열빛까지 그리려고 판정 지름보다 크게) */
const HALO = 1.2;
const EDGE = 1 / HALO;
/** 장판이 식으며 옅어지는 마지막 구간(0.5초 = 30틱) */
export const HELL_FADE_TICKS = 30;
/** 룬 원·충격파 판 크기 배율(판 반지름 1 = 판정 반지름 × 이 값) */
const RUNE_SCALE = 1.25;
const SHOCK_LIFE = 0.55;
const PILLAR_LIFE = 0.5;

/** 장판 페이드(0..1): 남은 틱이 FADE 이하일 때 선형으로 0까지 */
export function poolFade(ttl: number): number { return Math.max(0, Math.min(1, ttl / HELL_FADE_TICKS)); }

function hellPoolMaterial(t: N): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
  m.forceSinglePass = true;
  const F: N = attribute('aHell', 'vec4'); // (옅어짐, 열, 씨앗, 나이 초)
  const fade = F.x, heat = F.y, seed = F.z, age = F.w;
  const p = uv().sub(0.5).mul(2);
  const r = p.length();
  const a = atan(p.y, p.x);
  const spread = smoothstep(float(0), float(0.2), age).mul(0.3).add(0.7);
  const wob = sin(a.mul(8).add(t.mul(2.6)).add(seed.mul(9))).mul(0.03).add(sin(a.mul(15).sub(t.mul(3.4))).mul(0.02));
  const edge = float(EDGE).add(wob).mul(spread);
  const inside = float(1).sub(smoothstep(edge.sub(0.035), edge, r));
  // 균열: 두 겹 사인 잡음의 0 근처가 그물 같은 금 → 진홍으로 빛난다
  const q = p.add(vec2(seed.mul(5), seed.mul(11)));
  const n1 = sin(q.x.mul(6.3).add(t.mul(0.5)).add(sin(q.y.mul(4.9).sub(t.mul(0.4))).mul(1.4)))
    .mul(sin(q.y.mul(5.7).sub(t.mul(0.45)).add(sin(q.x.mul(4.1).add(t.mul(0.35))).mul(1.4))));
  const n2 = sin(q.x.add(q.y).mul(11.5).sub(t.mul(1.2))).mul(0.3).add(sin(q.x.sub(q.y).mul(9.1).add(t.mul(0.9))).mul(0.22));
  const lava = n1.mul(0.7).add(n2);
  const crack = float(1).sub(smoothstep(float(0), mix(float(0.05), float(0.16), heat), lava.abs()));
  // 가장자리 불꽃 테(주황) — 일렁이는 둑
  const rim = smoothstep(edge.sub(0.26), edge.sub(0.03), r).mul(inside);
  const halo = smoothstep(edge.add(0.16), edge, r).mul(float(1).sub(inside)).mul(0.4);
  const crimson = vec3(1.55, 0.1, 0.035), orange = vec3(1.8, 0.5, 0.06), crust = vec3(0.025, 0.004, 0.008);
  const flick = sin(t.mul(8).add(a.mul(5)).add(r.mul(10))).mul(0.14).add(0.86);
  const crackCol = mix(vec3(0.7, 0.04, 0.02), crimson, heat).mul(flick);
  const rimCol = mix(vec3(0.9, 0.18, 0.02), orange, heat).mul(flick);
  const body = mix(crust, crackCol, crack.mul(0.95));
  const withRim = mix(body, rimCol, rim.mul(0.9));
  m.colorNode = mix(withRim, rimCol.mul(0.7), float(1).sub(inside));
  m.opacityNode = inside.mul(0.95).add(halo).mul(fade).clamp(0, 1);
  return m;
}

/** 새김 룬 원: 고정 경계 고리 + 조여드는 고리 + 회전 룬 눈금 + 안쪽 별 무늬. 가산 혼합 */
function runeMaterial(t: N, prog: N, frenzy: N): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
  m.forceSinglePass = true; m.fog = false;
  const p = uv().sub(0.5).mul(2);
  const r = p.length();
  const a = atan(p.y, p.x);
  const R0 = float(1 / RUNE_SCALE); // 판정 반지름
  const line = (rr: N, c: N, w: number): N => float(1).sub(smoothstep(float(w * 0.5), float(w), rr.sub(c).abs()));
  const edge = line(r, R0, 0.05);
  // 조여드는 고리: 바깥(판 가장자리)에서 판정 반지름까지
  const shrinkR = mix(float(0.99), R0.mul(0.9), prog);
  const shrink = line(r, shrinkR, 0.07).mul(smoothstep(float(0), float(0.12), prog));
  // 회전 룬 눈금(안쪽 띠), 광폭이면 더 빨리 돈다
  const spin = t.mul(mix(float(0.9), float(2.2), frenzy));
  const tickPhase = fract(a.add(spin).div(Math.PI / 6)).sub(0.5).abs(); // 0(눈금 중심)..0.5
  const ticks = float(1).sub(smoothstep(float(0.06), float(0.14), tickPhase)).mul(line(r, R0.mul(0.78), 0.1));
  // 안쪽 별 무늬: 5각 조임
  const star = float(1).sub(smoothstep(float(0.02), float(0.07), r.sub(R0.mul(0.5).mul(float(0.82).add(sin(a.mul(5).sub(spin)).abs().mul(0.18)))).abs()));
  const fill = smoothstep(R0, float(0), r).mul(0.1).mul(prog);
  const shape = edge.mul(0.8).add(shrink).add(ticks.mul(0.7)).add(star.mul(0.55).mul(prog.mul(0.6).add(0.4))).add(fill);
  const warm = mix(vec3(1.5, 0.12, 0.04), vec3(1.9, 0.5, 0.06), prog);
  m.colorNode = mix(warm, vec3(1.2, 0.05, 0.1), frenzy.mul(0.5));
  m.opacityNode = shape.mul(smoothstep(float(0), float(0.1), prog)).mul(0.9).clamp(0, 1);
  return m;
}

/** 충격파 링: 바깥으로 퍼지며 가늘어지고 옅어진다. 가산 혼합 */
function shockMaterial(k: N): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
  m.forceSinglePass = true; m.fog = false;
  const p = uv().sub(0.5).mul(2);
  const r = p.length();
  const ringR = mix(float(0.15), float(0.97), float(1).sub(float(1).sub(k).pow(2.2)));
  const w = mix(float(0.16), float(0.04), k);
  const ring = float(1).sub(smoothstep(w.mul(0.3), w, r.sub(ringR).abs()));
  m.colorNode = mix(vec3(1.9, 0.55, 0.08), vec3(1.3, 0.08, 0.03), k);
  m.opacityNode = ring.mul(float(1).sub(k).pow(1.4)).mul(0.9).clamp(0, 1);
  return m;
}

export class HellFx {
  readonly group = new THREE.Group();
  /** 카메라 펀치 연결(px, ms, dx, dz). 없으면 생략 */
  onPunch: ((px: number, ms: number, dx: number, dz: number) => void) | null = null;
  private readonly pools: THREE.InstancedMesh;
  private readonly aHell: THREE.InstancedBufferAttribute;
  private readonly rune: THREE.Mesh;
  private readonly shock: THREE.Mesh;
  private readonly particles: RaidParticles;
  private readonly time = uniform(0);
  private readonly prog = uniform(0);
  private readonly frenzy = uniform(0);
  private readonly shockK = uniform(1);
  private readonly mtx = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly flat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
  private readonly spin = new THREE.Quaternion();
  private readonly yAxis = new THREE.Vector3(0, 1, 0);
  private readonly pos = new THREE.Vector3();
  private readonly scl = new THREE.Vector3();
  /** 룬 원 상태: 남은/전체 경고 초(세계 시계) */
  private runeLeft = 0;
  private runeTotal = 1;
  /** 충격파: 경과 초 */
  private shockAge = 99;
  private reduceFlash = false;
  /** 장판별 불씨 방출 누적(초) */
  private readonly emitAcc = new Float32Array(HELL_BRAND.max);

  constructor(particleScale: number) {
    this.group.name = 'hell-fx';
    const cap = HELL_BRAND.max;
    const pg = new THREE.PlaneGeometry(1, 1);
    this.aHell = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    this.aHell.setUsage(THREE.DynamicDrawUsage);
    pg.setAttribute('aHell', this.aHell);
    this.pools = new THREE.InstancedMesh(pg, hellPoolMaterial(this.time), cap);
    this.pools.name = 'fx-hell-pool';
    this.pools.count = 0; this.pools.visible = false; this.pools.frustumCulled = false; this.pools.renderOrder = 4;
    this.pools.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

    const rg = new THREE.PlaneGeometry(1, 1); rg.rotateX(-Math.PI / 2);
    this.rune = new THREE.Mesh(rg, runeMaterial(this.time, this.prog, this.frenzy));
    this.rune.name = 'fx-hell-rune'; this.rune.renderOrder = 5; this.rune.frustumCulled = false; this.rune.visible = false;
    const sg = new THREE.PlaneGeometry(1, 1); sg.rotateX(-Math.PI / 2);
    this.shock = new THREE.Mesh(sg, shockMaterial(this.shockK));
    this.shock.name = 'fx-hell-shock'; this.shock.renderOrder = 6; this.shock.frustumCulled = false; this.shock.visible = false;

    this.particles = new RaidParticles('add', Math.max(0.25, Math.min(0.3, particleScale)), 'fx-hell-add');
    this.particles.scale = Math.max(0.25, Math.min(1, particleScale));
    this.group.add(this.pools, this.rune, this.shock, this.particles.mesh);
    this.group.visible = false;
  }

  setReduceFlash(on: boolean): void { this.reduceFlash = on; this.particles.dim = on ? 0.6 : 1; }

  /** 새김/폭발 이벤트 */
  onEvent(e: GameEvent): void {
    if (e.type !== Ev.HellBrand) return;
    const P = this.particles;
    if (e.a === HellBrandPhase.Mark) {
      this.runeTotal = Math.max(0.2, e.c / 60); this.runeLeft = this.runeTotal;
      this.frenzy.value = e.d ? 1 : 0;
      const s = e.b * RUNE_SCALE * 2;
      this.rune.position.set(e.x, 0.07, e.z); this.rune.scale.set(s, 1, s);
      this.rune.visible = true;
      this.group.visible = true;
      // 새김 순간 바닥에서 올라오는 불씨 몇 개(8개)
      for (let i = 0, n = P.count(8); i < n; i++) {
        const a = P.rnd() * Math.PI * 2, rr = e.b * (0.7 + P.rnd() * 0.3);
        P.emit(AddKind.Ember, Orient.Camera, e.x + Math.cos(a) * rr, 0.1, e.z + Math.sin(a) * rr, 0, P.range(1.2, 2.4), 0, P.range(0.5, 0.9), 0.1, 0.03, 0xff3a14, 2.2, 0.9, 1, 0.3);
      }
      return;
    }
    if (e.a !== HellBrandPhase.Burst) return;
    this.runeLeft = 0; this.rune.visible = false;
    this.group.visible = true;
    // 충격파 링: 판정 반경에서 시작해 1.5배까지 퍼진다
    const s = e.b * 2 * 1.5;
    this.shock.position.set(e.x, 0.09, e.z); this.shock.scale.set(s, 1, s);
    this.shockAge = 0; this.shockK.value = 0; this.shock.visible = true;
    // 불기둥 2개(겹쳐 두껍게) + 광휘 1 + 불꽃 덩이 4 + 불씨 16 + 불똥 줄 10
    P.emit(AddKind.Pillar, Orient.Upright, e.x, 1.6, e.z, 0, 0.6, 0, PILLAR_LIFE, 1.5, 0.9, 0xff4a10, 2.4, 0.95, 0, 0, 0, 2.4);
    P.emit(AddKind.Pillar, Orient.Upright, e.x, 1.2, e.z, 0, 0.4, 0, PILLAR_LIFE * 0.8, 2.2, 1.4, 0xb01008, 1.8, 0.8, 0, 0, 0, 1.6);
    P.emit(AddKind.Flare, Orient.Camera, e.x, 0.5, e.z, 0, 0, 0, 0.22, 1.6, 2.6, 0xff6a20, this.reduceFlash ? 1.2 : 2.0, 0.8);
    for (let i = 0, n = P.count(4); i < n; i++) {
      const a = P.rnd() * Math.PI * 2, rr = P.rnd() * e.b * 0.6;
      P.emit(AddKind.Flame, Orient.Camera, e.x + Math.cos(a) * rr, 0.5, e.z + Math.sin(a) * rr, 0, P.range(1.5, 3), 0, P.range(0.45, 0.7), 0.7, 1.3, 0xff3a0c, 1.9, 0.85, 0, 1);
    }
    for (let i = 0, n = P.count(16); i < n; i++) {
      const a = P.rnd() * Math.PI * 2, sp = P.range(1.5, 4.5);
      P.emit(AddKind.Ember, Orient.Camera, e.x, 0.3, e.z, Math.cos(a) * sp, P.range(2.5, 6), Math.sin(a) * sp, P.range(0.7, 1.3), 0.12, 0.03, 0xff5a14, 2.4, 1, -7, 0.6);
    }
    for (let i = 0, n = P.count(10); i < n; i++) {
      const a = P.rnd() * Math.PI * 2, sp = P.range(4, 8);
      P.emit(AddKind.Streak, Orient.Velocity, e.x, 0.25, e.z, Math.cos(a) * sp, P.range(1.5, 4), Math.sin(a) * sp, P.range(0.3, 0.55), 0.08, 0.04, 0xff7a20, 2.2, 0.9, -8, 0.4, 0.06);
    }
    // 아주 작은 카메라 펀치(화면 전체 흔들림 아님). 섬광 줄이기 켜면 생략
    if (!this.reduceFlash && this.onPunch) this.onPunch(2, 110, 0, 1);
  }

  /** dt = 세계 시계 초, timeSec = 세계 시계 */
  update(view: GameView, dt: number, camera: THREE.Camera, timeSec: number): void {
    this.time.value = timeSec;
    const hb = view.hellBrand;
    const P = this.particles;
    // 새김 룬 원: 경고 길이 동안 조여든다
    if (this.runeLeft > 0) {
      this.runeLeft = Math.max(0, this.runeLeft - dt);
      this.prog.value = 1 - this.runeLeft / this.runeTotal;
      this.rune.visible = true;
    } else if (this.rune.visible) this.rune.visible = false;
    // 판이 끝나면(보스방을 나감) 룬·충격파를 숨긴다
    if (!hb || !hb.active) { this.runeLeft = 0; this.rune.visible = false; }
    // 충격파
    if (this.shockAge < SHOCK_LIFE) {
      this.shockAge += dt;
      this.shockK.value = Math.min(1, this.shockAge / SHOCK_LIFE);
      if (this.shockAge >= SHOCK_LIFE) this.shock.visible = false;
    }
    // 장판
    let n = 0;
    const fa = this.aHell.array as Float32Array;
    if (hb && hb.active) {
      for (let i = 0; i < hb.pools.length && n < HELL_BRAND.max; i++) {
        const f = hb.pools[i];
        if (!f.active) continue;
        const d = f.radius * 2 * HALO;
        const age = (f.total - f.ttl) / 60;
        const grow = Math.min(1, age / 0.18 + 0.5);
        this.spin.setFromAxisAngle(this.yAxis, i * 1.7 + timeSec * 0.1);
        this.q.copy(this.spin).multiply(this.flat);
        this.pos.set(f.x, 0.05 + i * 0.002, f.z);
        this.scl.set(d * grow, d * grow, d * grow);
        this.mtx.compose(this.pos, this.q, this.scl);
        this.pools.setMatrixAt(n, this.mtx);
        // 열: 갓 생김 1 → 0.5초에 걸쳐 0.75, 마지막 0.5초에 0으로 식는다
        const heat = f.ttl < HELL_FADE_TICKS ? 0.75 * f.ttl / HELL_FADE_TICKS : 0.75 + 0.25 * Math.max(0, 1 - age / 0.5);
        fa[n * 4] = poolFade(f.ttl); fa[n * 4 + 1] = heat; fa[n * 4 + 2] = (i * 0.618) % 1; fa[n * 4 + 3] = age;
        // 상시 불씨: 장판마다 0.14초에 하나(식는 구간엔 멈춘다)
        if (f.ttl > HELL_FADE_TICKS && dt > 0) {
          this.emitAcc[i] += dt;
          if (this.emitAcc[i] >= 0.14) {
            this.emitAcc[i] = 0;
            const a = P.rnd() * Math.PI * 2, rr = Math.sqrt(P.rnd()) * f.radius * 0.9;
            P.emit(AddKind.Ember, Orient.Camera, f.x + Math.cos(a) * rr, 0.08, f.z + Math.sin(a) * rr, P.range(-0.3, 0.3), P.range(0.8, 1.8), P.range(-0.3, 0.3), P.range(0.6, 1.0), 0.09, 0.02, 0xff4410, 2.0, 0.85, 0.4, 0.2);
          }
        }
        n++;
      }
    }
    this.pools.count = n; this.pools.visible = n > 0;
    if (n > 0) { this.pools.instanceMatrix.needsUpdate = true; this.aHell.needsUpdate = true; }
    P.update(dt, camera, timeSec);
    this.group.visible = n > 0 || this.rune.visible || this.shock.visible || P.active > 0;
  }

  /** 새 판·재도전: 남은 연출 정리 */
  clear(): void {
    this.runeLeft = 0; this.rune.visible = false; this.shockAge = 99; this.shock.visible = false;
    this.particles.clear(); this.pools.count = 0; this.pools.visible = false; this.emitAcc.fill(0); this.group.visible = false;
  }

  prewarm(on: boolean): void {
    this.group.visible = on;
    if (on) {
      (this.aHell.array as Float32Array).set([1, 1, 0, 1], 0);
      this.pools.count = 1; this.pools.visible = true; this.rune.visible = true; this.shock.visible = true;
    } else { this.pools.count = 0; this.pools.visible = false; this.rune.visible = false; this.shock.visible = false; }
    this.particles.prewarm(on);
  }

  dispose(): void {
    this.pools.geometry.dispose(); (this.pools.material as THREE.Material).dispose(); this.pools.dispose();
    for (const m of [this.rune, this.shock]) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
    this.particles.dispose();
  }
}
